import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import {
  Menu,
  LayoutDashboard, PenSquare, IdCard, CalendarDays, Newspaper, Users2, UserCircle, Building2, MessageSquare,
} from 'lucide-react'
import { PortalDashboardView } from '@/components/Portal/PortalDashboardView'
import { PortalMessagesView } from '@/components/Portal/PortalMessagesView'
import { PortalSubmitView } from '@/components/Portal/PortalSubmitView'
import { PortalProfileView } from '@/components/Portal/PortalProfileView'
import { PortalInstitutionView } from '@/components/Portal/PortalInstitutionView'
import { PortalPressPassView } from '@/components/Portal/PortalPressPassView'
import { PortalNotificationBell } from '@/components/Portal/PortalNotificationBell'
import { PortalAccountMenu } from '@/components/Portal/PortalAccountMenu'
import { PortalSettingsView } from '@/components/Portal/PortalSettingsView'
import { PortalEventsView } from '@/components/Portal/PortalEventsView'
import { PortalAssociationView } from '@/components/Portal/PortalAssociationView'
import { PortalPressReleaseView } from '@/components/Portal/PortalPressReleaseView'
import { PortalSocialHubView } from '@/components/Portal/PortalSocialHubView'
import { cn } from '@/lib/utils'

export default function PortalShell() {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Detect slug from URL: /orgs/:slug/portal/...
  const slugMatch = location.pathname.match(/^\/orgs\/([^/]+)\/portal/)
  const orgSlug = slugMatch?.[1] ?? null
  const basePath = orgSlug ? `/orgs/${orgSlug}/portal` : '/portal'

  // Load org branding by slug (white-label)
  const { data: orgBrand } = useQuery({
    queryKey: ['portal-org-brand', orgSlug],
    enabled: !!orgSlug,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('name, logo_url, brand_accent')
        .eq('slug', orgSlug!)
        .single() as any)
      return data as { name: string; logo_url: string | null; brand_accent: string | null } | null
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: profile } = useQuery({
    queryKey: ['journalist-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_profiles')
        .select('full_name, account_type')
        .eq('id', user.id)
        .single() as any)
      return data as { full_name: string; account_type: string } | null
    }
  })

  // Check if this user is also an organisation member (institution team)
  const { data: orgMembership } = useQuery({
    queryKey: ['portal-org-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('organization_members' as any)
        .select('org_id, roles(name)')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle() as any)
      return data as { org_id: string; roles: { name: string } } | null
    },
  })

  const isOrgMember = !!orgMembership

  const accentColor = orgBrand?.brand_accent ?? '#C8A84B'

  interface NavItem { to: string; end?: boolean; icon: typeof LayoutDashboard; label: string }

  const navItems: NavItem[] = [
    { to: basePath, end: true, icon: LayoutDashboard, label: 'My Dashboard' },
    ...(isOrgMember ? [{ to: `${basePath}/social-hub`, icon: Users2, label: 'Social Hub' }] : []),
    { to: `${basePath}/submit`, icon: PenSquare, label: 'Submit Content' },
    { to: `${basePath}/passes`, icon: IdCard, label: 'My Passes' },
    { to: `${basePath}/messages`, icon: MessageSquare, label: 'Messages' },
    { to: `${basePath}/events`, icon: CalendarDays, label: 'Events' },
    { to: `${basePath}/press-releases`, icon: Newspaper, label: 'Press Releases' },
    { to: `${basePath}/membership`, icon: Building2, label: 'Membership' },
    { to: `${basePath}/profile`, icon: UserCircle, label: 'My Profile' },
    ...(profile?.account_type === 'institution'
      ? [{ to: `${basePath}/institution`, icon: Building2, label: 'Institution' }]
      : []),
  ]

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-11',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
      isActive
        ? 'bg-[var(--portal-accent)]/10 text-[var(--portal-accent)]'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    )

  const navList = (onNavigate?: () => void) => (
    <>
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={navItemClass}
          onClick={onNavigate}
        >
          <item.icon className="shrink-0" style={{ width: 18, height: 18 }} />
          {item.label}
        </NavLink>
      ))}
    </>
  )

  return (
    // Inject accent colour as a CSS var so nav items can reference it without inline style per-link
    <div
      className="min-h-screen bg-background flex"
      style={{ ['--portal-accent' as any]: accentColor }}
    >
      {/* Vertical sidebar, desktop */}
      <aside className="hidden md:flex w-64 shrink-0 bg-sidebar border-r border-sidebar-border flex-col">
        <div className="px-5 pt-5 pb-4 border-b border-sidebar-border/50">
          {orgBrand?.logo_url
            ? <img src={orgBrand.logo_url} alt={orgBrand.name} className="h-8 object-contain" />
            : <img src="/conorbit-logo.png" alt="CONORBIT" className="h-8" />
          }
          <p className="text-justify text-xs text-muted-foreground mt-1">Journalist Portal</p>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {navList()}
        </nav>

        <div className="p-3 border-t border-sidebar-border">
          <p className="text-xs text-muted-foreground truncate px-1">{profile?.full_name ?? '…'}</p>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Sticky top bar, all breakpoints */}
        <header className="bg-background border-b border-border sticky top-0 z-10">
          <div className="px-4 md:px-6 h-14 flex items-center justify-between">
            <span className="md:hidden">
              {orgBrand?.logo_url
                ? <img src={orgBrand.logo_url} alt={orgBrand.name} className="h-7 object-contain" />
                : <img src="/conorbit-logo.png" alt="CONORBIT" className="h-7" />
              }
            </span>
            <span className="hidden md:block" />
            <div className="flex items-center gap-2">
              <PortalNotificationBell />
              <PortalAccountMenu basePath={basePath} orgSlug={orgSlug} />
              <button
                className="md:hidden h-11 w-11 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation menu"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>
            </div>
          </div>
        </header>

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-72 p-0 flex flex-col bg-sidebar border-sidebar-border">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <div className="px-5 pt-5 pb-4 border-b border-sidebar-border/50">
              {orgBrand?.logo_url
                ? <img src={orgBrand.logo_url} alt={orgBrand.name} className="h-8 object-contain" />
                : <img src="/conorbit-logo.png" alt="CONORBIT" className="h-8" />
              }
              <p className="text-xs text-muted-foreground mt-1">Journalist Portal</p>
            </div>
            <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
              {navList(() => setMobileMenuOpen(false))}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Content */}
        <main className="flex-1 w-full px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-5xl mx-auto">
          <Routes>
            <Route index element={<PortalDashboardView />} />
            <Route path="submit" element={<PortalSubmitView />} />
            <Route path="passes" element={<PortalPressPassView />} />
            <Route path="messages" element={<PortalMessagesView />} />
            <Route path="events" element={<PortalEventsView />} />
            <Route path="press-releases" element={<PortalPressReleaseView />} />
            <Route path="membership" element={<PortalAssociationView />} />
            <Route path="profile" element={<PortalProfileView />} />
            <Route path="settings" element={<PortalSettingsView />} />
            <Route path="social-hub" element={<PortalSocialHubView />} />
            <Route path="institution" element={<PortalInstitutionView />} />
            <Route path="*" element={<Navigate to={basePath} replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
